import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import './CookieConsent.css';

/**
 * Ask Irene — consent gate for analytics.
 *
 * Design rules:
 *  - PostHog is opted OUT until the visitor clicks Accept. Reject means no events, period.
 *  - Global Privacy Control (browser-level opt-out signal) is honored as a Reject.
 *  - Rejecting also sweeps any PostHog cookies/storage left over from before this shipped.
 *  - The only thing stored without consent is the consent choice itself (strictly necessary).
 *  - Nothing here touches Supabase auth, Stripe, or Sentry.
 */

const CONSENT_KEY = 'askirene_consent_v1';
const CONSENT_VERSION = 1;

const ConsentContext = createContext({
  status: 'pending',
  openSettings: () => {},
});

export const useConsent = () => useContext(ConsentContext);

/* ---------------------------------------------------------------- storage */

function readStored() {
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      parsed.v === CONSENT_VERSION &&
      (parsed.status === 'granted' || parsed.status === 'denied')
    ) {
      return parsed.status;
    }
  } catch (e) {
    /* private mode / blocked storage — treat as no choice made */
  }
  return null;
}

function writeStored(status) {
  try {
    window.localStorage.setItem(
      CONSENT_KEY,
      JSON.stringify({ v: CONSENT_VERSION, status, at: new Date().toISOString() })
    );
  } catch (e) {
    /* non-fatal: the banner will simply ask again next visit */
  }
}

function gpcEnabled() {
  try {
    return window.navigator.globalPrivacyControl === true;
  } catch (e) {
    return false;
  }
}

/* --------------------------------------------------------------- posthog */

const ph = () => (typeof window !== 'undefined' ? window.posthog : undefined);

function enableAnalytics() {
  const p = ph();
  if (!p) return;
  try {
    p.set_config({ persistence: 'localStorage+cookie' });
    p.opt_in_capturing();
    // init runs with capture_pageview:false, so send the first one by hand.
    p.capture('$pageview');
  } catch (e) {
    /* analytics must never break the app */
  }
}

function disableAnalytics() {
  const p = ph();
  try {
    if (p) {
      p.opt_out_capturing();
      p.set_config({ persistence: 'memory' });
    }
  } catch (e) {
    /* ignore */
  }
  clearAnalyticsStorage();
}

/** Removes PostHog cookies/storage, including any set before consent shipped. */
function clearAnalyticsStorage() {
  const isPostHogKey = (name) => /^(ph_|__ph)/.test(name);

  try {
    const host = window.location.hostname;
    const parts = host.split('.');
    const apex = parts.length > 1 ? parts.slice(-2).join('.') : host;
    const domains = [null, host, `.${host}`, apex, `.${apex}`];

    document.cookie.split(';').forEach((chunk) => {
      const name = chunk.split('=')[0].trim();
      if (!name || !isPostHogKey(name)) return;
      domains.forEach((domain) => {
        document.cookie = `${name}=; Max-Age=0; path=/${
          domain ? `; domain=${domain}` : ''
        }`;
      });
    });
  } catch (e) {
    /* ignore */
  }

  [
    typeof window !== 'undefined' ? window.localStorage : null,
    typeof window !== 'undefined' ? window.sessionStorage : null,
  ].forEach((store) => {
    if (!store) return;
    try {
      Object.keys(store).forEach((key) => {
        if (isPostHogKey(key)) store.removeItem(key);
      });
    } catch (e) {
      /* ignore */
    }
  });
}

/* -------------------------------------------------------------- provider */

export function CookieConsentProvider({ children }) {
  const [status, setStatus] = useState('pending');
  const [visible, setVisible] = useState(false);
  const bannerRef = useRef(null);
  const lastTrigger = useRef(null);
  const shouldFocus = useRef(false);

  useEffect(() => {
    const stored = readStored();

    if (stored === 'granted') {
      setStatus('granted');
      enableAnalytics();
      return;
    }

    if (stored === 'denied') {
      setStatus('denied');
      disableAnalytics();
      return;
    }

    // No explicit choice yet. A browser-level opt-out signal counts as Reject,
    // and we don't persist it — if they turn GPC off, they get asked normally.
    if (gpcEnabled()) {
      setStatus('denied');
      disableAnalytics();
      return;
    }

    setStatus('pending');
    disableAnalytics();
    setVisible(true);
  }, []);

  useEffect(() => {
    if (visible && shouldFocus.current && bannerRef.current) {
      bannerRef.current.focus();
      shouldFocus.current = false;
    }
  }, [visible]);

  const decide = useCallback((next) => {
    setStatus(next);
    writeStored(next);
    if (next === 'granted') enableAnalytics();
    else disableAnalytics();
    setVisible(false);

    // Return focus to whatever opened the panel (footer link), per WCAG 2.4.3.
    const trigger = lastTrigger.current;
    lastTrigger.current = null;
    if (trigger && document.contains(trigger)) {
      trigger.focus();
    }
  }, []);

  const openSettings = useCallback((triggerEl) => {
    lastTrigger.current = triggerEl || null;
    shouldFocus.current = true;
    setVisible(true);
  }, []);

  const currentLabel =
    status === 'granted'
      ? 'Analytics are currently on.'
      : status === 'denied'
      ? 'Analytics are currently off.'
      : null;

  return (
    <ConsentContext.Provider value={{ status, openSettings }}>
      {children}

      {visible && (
        <div
          className="cc-banner"
          role="dialog"
          aria-modal="false"
          aria-labelledby="cc-title"
          aria-describedby="cc-desc"
          tabIndex={-1}
          ref={bannerRef}
        >
          <div className="cc-inner">
            <div className="cc-copy">
              <h2 className="cc-title" id="cc-title">
                Cookies and analytics
              </h2>
              <p className="cc-desc" id="cc-desc">
                We use privacy-friendly analytics to understand which pages
                help people. We never send your conversations, bills, or
                personal details to analytics. Cookies required to sign in and
                process payments are always on.{' '}
                <a className="cc-link" href="/privacy">
                  Read our privacy policy
                </a>
                .
                {currentLabel ? (
                  <span className="cc-current"> {currentLabel}</span>
                ) : null}
              </p>
            </div>

            <div className="cc-actions">
              <button
                type="button"
                className="cc-btn"
                onClick={() => decide('granted')}
              >
                Accept analytics
              </button>
              <button
                type="button"
                className="cc-btn"
                onClick={() => decide('denied')}
              >
                Reject analytics
              </button>
            </div>
          </div>
        </div>
      )}
    </ConsentContext.Provider>
  );
}

/* ------------------------------------------------------- footer trigger */

export function CookieSettingsButton({ className = '', children }) {
  const { openSettings } = useConsent();
  return (
    <button
      type="button"
      className={`cc-settings-link ${className}`.trim()}
      onClick={(e) => openSettings(e.currentTarget)}
    >
      {children || 'Cookie settings'}
    </button>
  );
}

export default CookieConsentProvider;
